import { useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

interface SelectFieldProps {
  label: string;
  required?: boolean;
  placeholder?: string;
  value?: string;
  options: readonly string[];
  className?: string;
  onSelect: (value: string) => void;
}

export function SelectField({
  label,
  required,
  placeholder,
  value,
  options,
  className,
  onSelect,
}: SelectFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold tracking-wide text-field">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              className={cn(
                className,
                "justify-between hover:bg-primary-soft",
                !value && "text-border!",
                open && "bg-accent!",
              )}
            >
              <span className="min-w-0 flex-1 truncate text-left">
                {value || placeholder}
              </span>
              <ChevronDown
                size={16}
                className={cn(
                  "transition-transform duration-200 ease-in-out text-secondary-foreground",
                  open && "rotate-180",
                )}
                aria-hidden="true"
              />
            </Button>
          }
        />
        <PopoverContent
          align="start"
          className="w-(--anchor-width) max-h-60 overflow-y-auto rounded-2xl shadow-xl"
        >
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onSelect(option);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-primary-soft",
                value === option && "bg-primary-soft",
              )}
            >
              {option}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
